import { z } from "zod";
import {
  AuditorTrailSchema,
  OutcomeType,
  PerformanceIssueType,
} from "./attributeTestColumn";
import {
  analyticsControlException,
  analyticsPopulationSource,
  analyticsReviewerType,
  analyticsSupersessionReason,
} from "./analyticsEnums";

/**
 * The judge provenance group. The four move together: a judge ran and every
 * field is set, or none ran and every field is null. A verdict an auditor
 * reached and one the pipeline declined to judge both carry the group empty, so
 * the group's presence is exactly the claim that a judge ran.
 */
const JUDGE_FIELDS = [
  "judgeModelId",
  "judgeEffort",
  "judgePromptVersion",
  "criteriaFingerprint",
] as const;

/**
 * The supersession group. The three move together: a verdict is either live (all
 * null) or superseded (all set) — there is no half-superseded verdict.
 */
const SUPERSESSION_FIELDS = [
  "supersededAt",
  "supersededById",
  "supersessionReason",
] as const;

/**
 * One operation of the run, as the stored log records it.
 *
 * The stored log is the run's own payload in the shape it arrived, so these keys
 * are the engine's rather than Midship's. Only the four a surface reads are
 * modelled: the step id is the join to that step's card, the name is what the
 * step is called, a null completion is the only mark a stopped run leaves on the
 * step it stopped at, and the operation is what the step did, which is what the
 * review pane's rows take their glyph from.
 *
 * `operation` falls back to null rather than failing the entry, and the
 * asymmetry with the keys above is deliberate: the id, the name and the
 * completion are what a log entry *is*, while the operation only decorates a row
 * that draws fine without it. A run whose log predates the key, or that spells
 * it as something other than a string, is a run whose steps still have names and
 * times — refusing the whole verdict over a glyph would cost a reader the
 * account of the run to save them an unlabelled tile.
 */
const analyticsVerdictStepLogEntrySchema = z.object({
  stepId: z.string(),
  stepName: z.string(),
  completedAt: z.string().nullable().default(null),
  operation: z.string().nullable().catch(null),
});

/**
 * The run this verdict was judged over, as the verdict carries it.
 *
 * Narrower than `analyticsRunManifestSchema`, which models the stored record
 * whole; this is the read set of the surfaces that open a verdict, and it is the
 * one the wire hands over without a second round trip.
 *
 * `stepLog` is the run's own operation log. Drift is refused in whole rather
 * than in part — `.catch(null)` over the array, not over its elements — because
 * the backend's normaliser refuses it that way and for the same reason: a log
 * read half-way renders a run as having done less than it did, and the step
 * cards resolved from it would silently be a subset. `null` is therefore "no log
 * this build can read", which is a different fact from `[]`, a run that logged
 * no operations.
 *
 * **The run's published files are not here, and belong to `/run-manifest`.** One
 * backend record projected twice into one pane is two lists free to disagree,
 * and the disagreement is not symmetric: this side would have to default an
 * omitted `outputs` to `[]`, which is indistinguishable from a run that
 * published none, and the claim resting on it — that the run published
 * nothing — is the stronger of the two. The route projection carries the file
 * id, size, format and ordinal a reader needs anyway, so there is one list and
 * it is that one.
 */
const analyticsVerdictRunManifestSchema = z.object({
  id: z.string(),
  // Half of a step card's identity: a card is matched by the run it belongs to
  // and the step within it, so one run's evidence can never be served for
  // another run's step.
  automationWorkflowRunId: z.string(),
  automationWorkflowVersionId: z.string().nullable().default(null),
  // The label AuditBoard shows for that version, which is what a reader is
  // shown; the id above is a tenant-wide row id. Defaulted so a backend that
  // does not yet serve it parses as "no label recorded".
  automationWorkflowVersionLabel: z.string().nullable().default(null),

  populationTotal: z.number().nullable().default(null),
  populationSource: analyticsPopulationSource.nullable().catch(null),
  populationStepId: z.string().nullable().default(null),
  populationInputParamKey: z.string().nullable().default(null),

  stepLog: z.array(analyticsVerdictStepLogEntrySchema).nullable().catch(null),
});

const analyticsColumnResultShape = z.object({
  id: z.string(),
  analyticsColumnId: z.string(),

  /**
   * A flat shape rather than a discriminated union on `isConclusive`. The
   * attribute-test sibling narrows because its arms differ in field set, so the
   * discriminant gates access to fields one arm lacks; here the arms differ only
   * in whether `outcome` is null, every reader reads the same fields on both, and
   * a union would force the cross-field `superRefine` below to be duplicated per
   * arm. `outcome` is therefore nullable rather than required-on-one-arm.
   */
  isConclusive: z.boolean(),

  /**
   * Falls back to null rather than failing, so a verdict whose outcome this
   * build cannot read still reaches its own cell instead of being dropped and
   * reading as untested. The fallback cannot flatter the wire: the badge
   * translator maps a conclusive verdict carrying no outcome to a failure, never
   * to a pass.
   */
  outcome: OutcomeType.nullable().catch(null),
  outcomeDescription: z.string().nullable(),
  evidenceSummary: z.string().nullable(),

  /**
   * Coarsens an unrecognised member exactly as the grid projection does, so one
   * verdict cannot badge from the table shell and then fail to open here. What
   * the coarsening costs is the raw member: no surface keeps it, and none tells
   * a coarsened exception apart from one the wire genuinely reported as the
   * catch-all.
   *
   * Required rather than defaulted: this surface relies on the key always
   * being present on the wire, so an absent `controlExceptions` is treated as
   * a contract break. Defaulting to `[]` would instead read as the positive
   * claim that testing found no exceptions — worth failing on rather than
   * papering over.
   */
  controlExceptions: z.array(analyticsControlException),
  partialDescription: z.string().nullable(),

  /**
   * Why testing could not conclude, on an inconclusive verdict.
   *
   * An unrecognised member is discarded and the verdict survives, rather than
   * failing its element and taking the whole verdict — and with it the cell —
   * down. It is dropped rather than coarsened the way `controlExceptions` is,
   * because the two vocabularies differ in what they can say:
   * `ControlExceptionType` carries a catch-all that honestly means "outside the
   * named categories", so coarsening to it states something weaker but still
   * true, while `PerformanceIssueType`'s one member is the specific claim that a
   * search ran and found nothing. Coarsening here would invent a cause the
   * pipeline never reported.
   *
   * The cost is that a reason this build cannot name becomes invisible instead
   * of wrong: the pane lists one fewer reason than the pipeline gave, and the
   * verdict's own prose is what still explains why testing did not conclude.
   */
  performanceIssues: z
    .array(PerformanceIssueType.nullable().catch(null))
    .default([])
    .transform((issues) => issues.filter((issue) => issue !== null)),
  potentialMitigatingFactors: z.string().nullable(),

  /** Rows the workflow ran over, so "tested 42,318, no sampling" is read, not inferred. */
  populationTotal: z.number().nullable(),

  /**
   * The AuditBoard workflow run this verdict was judged over. Nullable: a
   * verdict an auditor reached names no manifest, and the backend requires one
   * only of a judged verdict.
   *
   * Kept beside `runManifest` rather than replaced by it: the id is on the row
   * itself and is present whether or not the manifest was hydrated, so a surface
   * asking only "is there a run behind this" reads it without depending on the
   * hydration. The verdict's own `run_id` — the Midship `session_run` — stays
   * unmodelled: it is a different fact from the manifest id rather than a second
   * path to it, and the grid projection carries it because a cell clears a
   * failed run's error badge by comparing against it; the pane does nothing
   * with it.
   */
  runManifestId: z.string().nullable(),

  /**
   * What the run did, hydrated onto every read of a verdict.
   *
   * The review pane resolves this cell's step cards from it — the run id and the
   * step ids in its log are the whole of the step-to-card link — reads the
   * workflow version that actually executed for the workflow link, and reads the
   * population's provenance. None of that is reachable from the verdict's own
   * fields.
   *
   * `.catch(null)` rather than failing: the manifest is the evidence behind the
   * answer, not the answer, so a manifest whose shape has moved past this build
   * costs the evidence sections and leaves the conclusion and its figures
   * rendering. Failing would drop the verdict, and a dropped verdict reads as a
   * cell that was never tested.
   */
  runManifest: analyticsVerdictRunManifestSchema.nullable().catch(null),

  /**
   * The verdict's evidence trail: a conclusion line and ordered steps, each
   * carrying the citations it relies on.
   *
   * The same `AuditorTrail` an attribute-test verdict carries rather than an
   * analytics-shaped lookalike — the backend relaxed the trail to two parents
   * precisely so this is the existing type, and a second schema here would be
   * two spellings of one record free to disagree.
   *
   * The star lives here: a step card is the citable artifact, so "included in
   * the workpaper" is `isKey` on the citation anchored to that card. `.catch`
   * for the reason the manifest has one — a trail this build cannot read costs
   * the evidence, never the cell.
   */
  auditorTrail: AuditorTrailSchema.nullable().catch(null),

  columnVersion: z.number().default(0),
  // The human half of attribution: the user who reached this verdict when
  // `reviewerType` is human, null for system verdicts. `lockedBy` is the lock
  // holder, not the author.
  userId: z.string().nullable(),
  lockedBy: z.string().nullable().optional(),

  /**
   * Judge provenance. How the verdict was reached is not modelled, because it is
   * exactly reconstructible from what is here: the backend writes this group if
   * and only if a judge ran, and marks the reviewer human only for an auditor's
   * override. So a human reviewer means an auditor overrode, otherwise
   * provenance present means a judge ran, and otherwise none did. Modelling
   * the method as well would be a second, closed way to say what these fields
   * already say, and a member this build has not heard of would cost the verdict
   * that carries it.
   *
   * `verdictMethodOf` below is that reconstruction, written once so no surface
   * spells the rule out a second time.
   */
  judgeModelId: z.string().nullable(),
  judgeEffort: z.string().nullable(),
  judgePromptVersion: z.string().nullable(),
  criteriaFingerprint: z.string().nullable(),

  /**
   * Who is accountable for the verdict, and one of the two inputs the method is
   * reconstructed from — not a second, independent axis to be read alongside it.
   * The backend marks this human for an auditor's override and at no other time,
   * which is what makes `verdictMethodOf` able to tell an override from an
   * unjudged verdict.
   *
   * A surface asking how a mark was produced therefore calls that function
   * rather than reading this field: on its own it collapses a judgment, a
   * refusal and a mechanical read into the one member that answers who to ask
   * about them, and reading it for anything finer recovers a distinction it
   * never carried.
   *
   * Falls back to the agent rather than failing, so a writer category this build
   * cannot name costs the attribution line and not the verdict — the pane would
   * otherwise refuse to open a cell the grid has already badged. A `.catch` and
   * not a default: a default fires only on `undefined`, so it rescues an absent
   * field and never an unrecognised value, which is the case that arises here.
   *
   * The residual is real and is not closed here: if a third writer category ever
   * ships and it denotes a kind of human, two shared surfaces read the fallback
   * rather than the wire. The result card attributes those verdicts to the
   * agent, and the grid's staleness check exempts only a `human` verdict, so an
   * unlocked one whose column has since moved also takes a ⚠ an auditor's own
   * conclusion would not. Both are read by the eval and attribute-test cells
   * too, so rewording them is not this schema's to do.
   *
   * Deriving the fallback from whether `userId` is set would close the common
   * case, but it is not airtight and is deliberately not used: the backend's
   * `user_id` is `ON DELETE SET NULL`, so an override can legally outlive the
   * auditor who made it and present as a human verdict with no user.
   */
  reviewerType: analyticsReviewerType.catch("system"),

  // Supersession.
  supersededAt: z.string().nullable(),
  supersededById: z.string().nullable(),
  supersessionReason: analyticsSupersessionReason.nullable(),

  createdAt: z.string(),
  updatedAt: z.string(),
});

const isPresent = (value: unknown): boolean =>
  value !== null && value !== undefined;

/**
 * The judge group as one value, less the fingerprint the grid projection does
 * not send: a judge ran and every field is set, or none ran and every field is
 * null. Typed as the two arms rather than as three nullable fields so that no
 * reader can be handed a group that is half of each.
 */
export type JudgeProvenance =
  | { judgeModelId: string; judgeEffort: string; judgePromptVersion: string }
  | { judgeModelId: null; judgeEffort: null; judgePromptVersion: null };

/**
 * Resolves parsed judge fields to one `JudgeProvenance`, and is how every parse
 * boundary that carries the group hands it over.
 *
 * A group with any field unset resolves as absent, because a judgment cannot be
 * represented without inventing the fields it is missing. It is also the reading
 * that claims less: to an auditor a judgment is the stronger claim, so absent is
 * the direction to fail. On the full verdict that loses nothing: the refinement
 * below has already refused a part-set group. On the grid summary, which no
 * refinement guards, a part-set group arises only from a projection breaking the
 * wire contract — the store keeps the group whole, keyed to the verdict method —
 * so it is a contract break rather than a state to render, and it then reads a
 * judgment as a refusal rather than costing the row.
 */
export function judgeProvenanceOf(fields: {
  judgeModelId?: string | null;
  judgeEffort?: string | null;
  judgePromptVersion?: string | null;
}): JudgeProvenance {
  const { judgeModelId, judgeEffort, judgePromptVersion } = fields;
  if (
    typeof judgeModelId === "string" &&
    typeof judgeEffort === "string" &&
    typeof judgePromptVersion === "string"
  ) {
    return { judgeModelId, judgeEffort, judgePromptVersion };
  }
  return { judgeModelId: null, judgeEffort: null, judgePromptVersion: null };
}

/**
 * The verdict a published workflow reached for one sample, with the run it was
 * judged over and the trail it was reached through.
 *
 * Both provenance groups are all-or-nothing, enforced here rather than left to
 * each reader: a partially-populated group would render a provenance line that is
 * half-true, which is worse than one that is absent.
 *
 * The two hydrated halves — `runManifest` and `auditorTrail` — are outside those
 * refinements on purpose. Each is evidence the verdict points at rather than a
 * field of it, each degrades to null on its own, and neither absence makes the
 * other's presence a contradiction: a verdict an auditor reached names no
 * manifest and may still carry the trail the judgment it superseded was read
 * through.
 */
export const AnalyticsColumnResultSchema = analyticsColumnResultShape
  .superRefine((result, ctx) => {
    const judgeFieldsSet = JUDGE_FIELDS.filter((field) =>
      isPresent(result[field]),
    );

    if (
      judgeFieldsSet.length > 0 &&
      judgeFieldsSet.length !== JUDGE_FIELDS.length
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [
          JUDGE_FIELDS.find((field) => !isPresent(result[field])) ??
            "judgeModelId",
        ],
        message: "Judge provenance fields move together: all null, or all set.",
      });
    }

    const supersessionFieldsSet = SUPERSESSION_FIELDS.filter((field) =>
      isPresent(result[field]),
    );

    if (
      supersessionFieldsSet.length > 0 &&
      supersessionFieldsSet.length !== SUPERSESSION_FIELDS.length
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [supersessionFieldsSet[0]],
        message: "Supersession fields move together: all null, or all set.",
      });
    }
  })
  .transform((result) => ({ ...result, ...judgeProvenanceOf(result) }));

export type AnalyticsColumnResult = z.infer<typeof AnalyticsColumnResultSchema>;

/**
 * How an analytics verdict was produced.
 *
 * Derived, never parsed. No schema names it, because a closed vocabulary
 * declared at a parse boundary can be handed a member this build has not heard
 * of, and the only answers then are to drop the verdict — reporting a tested
 * cell as untested — or to resolve it to whichever member it most resembles,
 * which attributes a machine's mark to an auditor or the reverse.
 * Reconstruction has no such failure mode, because it reads fields that are
 * already load-bearing for other reasons.
 */
export type VerdictMethod =
  | "llm_judgment"
  | "human_override"
  | "pipeline_refusal";

/**
 * The fields the method is reconstructed from, declared as a structural subset
 * rather than as either wire shape, because both answer through one function:
 * the full verdict the review pane fetches, and the slim summary the grid cell
 * badges from. Two spellings of one rule would be free to disagree about the
 * cell an auditor is looking at while its pane opens over it.
 *
 * The judge group is required, and as one `JudgeProvenance`. A shape that merely
 * lacks the group — a badge's field set, say — would otherwise satisfy the
 * subset and read as a verdict no judge reached, and a shape carrying half of it
 * would leave the answer to whichever field a reader happened to check.
 */
export type VerdictProvenanceFields = Pick<
  AnalyticsColumnResult,
  "reviewerType"
> &
  JudgeProvenance;

/**
 * The one place the reconstruction is written. Every surface that needs to say
 * how a verdict was produced calls this; a second spelling of the rule would be
 * free to disagree with the first about a verdict neither arm's author had in
 * mind.
 *
 * Judge provenance is written if and only if a judge ran, and the reviewer is
 * marked human only for an auditor's own override, so: a human reviewer is an
 * override; otherwise provenance present is a judgment; otherwise the pipeline
 * declined to judge.
 *
 * The reviewer answers first because ownership is the attribution every column
 * kind reads: an attribute-test verdict carries no judge group at all and is
 * attributed through `reviewerType` alone, so asking the judge group first
 * would make analytics the one kind that attributes a verdict differently.
 * Asking it first would also attribute to the system any row carrying both a
 * human reviewer and the group, which nothing on this side rules out —
 * `JudgeProvenance` holds the group to all-or-nothing, not to being exclusive
 * with a human reviewer.
 *
 * The last arm is reached by elimination and is named for what the pipeline does
 * there rather than for the absence that identifies it. That is sound only
 * because the three are total over the verdicts this pipeline writes — every
 * analytics verdict is judged, overridden, or refused before the judge — which
 * is the same totality that lets the group's absence mean anything at all.
 *
 * `runManifestId` is not consulted and cannot be: a refusal names the run it
 * refused on and carries one too, so keying on it reports every refusal as a
 * judgment.
 *
 * One judge field answers for the group, because the parameter's type admits
 * only a whole group or an empty one, and every parse boundary that carries it
 * resolves it through `judgeProvenanceOf`.
 */
export function verdictMethodOf(
  result: VerdictProvenanceFields,
): VerdictMethod {
  if (result.reviewerType === "human") return "human_override";

  return result.judgeModelId === null ? "pipeline_refusal" : "llm_judgment";
}
